import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { AdlItem } from "@/hooks/queries/adl-item-types";

// ADL skill-item dictionary hook (Story 6.5, consuming Story 4.2's endpoint). Wraps
// `apiFetch` + `useQuery` — no manual fetch/useEffect (mirrors useAmpsTasks). The
// dictionary is static and server-cached 24h, so `staleTime: Infinity` fetches it ONCE
// per session — every tab's grid reads the same cached set rather than refetching.

/** Root key for the dictionary query. `version: "current"` keeps the cache keyed to the active set. */
export const ADL_ITEMS_QUERY_KEY = "adl-items";

export function useAdlItems() {
  return useQuery({
    queryKey: [ADL_ITEMS_QUERY_KEY, "current"],
    queryFn: () => apiFetch<AdlItem[]>("/reference/adl-items?version=current"),
    staleTime: Infinity,
  });
}

// --- Grid layout constants (shared by the grid + tests) ----------------------

/** Motor items occupy positions 1–16 of the clinician scale; process 17–35. */
export const MOTOR_ITEM_COUNT = 16;

/** The score grid lays the items out across 4 columns (Screen 06 / UX D2). */
export const SCORE_COLUMNS = 4;

/**
 * The code of the SECOND Paces row in the 36-row dictionary — process Paces, the
 * AMPS-standard second calibration of the single Paces skill (seed adl_items.py:36,
 * 395). It is collapsed OUT of the clinician scale (motor Paces `M16` is kept).
 */
const DUPLICATE_PACES_CODE = "P36";

// --- Dictionary → clinician-scale selector (pure, unit-testable) -------------

/**
 * The 35-item clinician scoring scale, derived from the canonical 36-row dictionary.
 *
 * The AMPS engine calibrates the skill "Paces" on BOTH the motor and process scales,
 * so the dictionary seeds it twice (`M16` motor + `P36` process) — 36 rows. The
 * clinician OBSERVES and rates Paces ONCE; that single score feeds both engine
 * positions at scoring/submit (Epic 9). So the scoring grid shows Paces once: 35 items.
 *
 * Rule: sort by `sequence`, then drop the duplicate process-Paces row (`code === "P36"`).
 * The result is Motor 1–16 (Paces once, at position 16) + Process 17–35 — the exact
 * client-validated Screen 06 order/labels. Lossless: the dropped row is identical in
 * meaning to the kept one (seed adl_items.py:40 — "collapse is a Story 6.5 decision").
 */
export function clinicianAdlItems(items: AdlItem[]): AdlItem[] {
  return [...items]
    .sort((a, b) => a.sequence - b.sequence)
    .filter((item) => item.code !== DUPLICATE_PACES_CODE);
}

// --- Score-value helpers (pure, shared by the grid + tests) ------------------
// The store holds scores as `number | null` (null = unscored); these operate at the
// INPUT-string boundary (1–4 hard validation, AC 3) and convert at the edge.

/** True when an input string is exactly one of "1"|"2"|"3"|"4" (the only committable values). */
export function isValidScore(value: string): boolean {
  return value === "1" || value === "2" || value === "3" || value === "4";
}

/** True when a non-empty input string is NOT a valid 1–4 score (the "invalid" cell state). */
export function isInvalidScore(value: string): boolean {
  return value !== "" && !isValidScore(value);
}

/** A fresh positional score array of `count` unscored (`null`) cells, index i ↔ item i. */
export function emptyScores(count: number): (number | null)[] {
  return Array<number | null>(count).fill(null);
}

/**
 * The length-`count` positional score array for a task, normalized for display/edit.
 *
 * Reads the persisted array (keyed by task code) and pads/truncates it to exactly
 * `count` cells so the grid is always index-aligned with the resolved clinician scale —
 * even for a draft persisted by an older shape (shorter array) or a malformed one.
 * `null` fills any missing cell (unscored), never a fabricated score.
 */
export function scoresForTask(
  scores: Record<string, (number | null)[]> | undefined,
  taskCode: string,
  count: number,
): (number | null)[] {
  const existing = scores?.[taskCode];
  const base = emptyScores(count);
  if (!Array.isArray(existing)) return base;
  for (let i = 0; i < count; i += 1) {
    const value = existing[i];
    base[i] = typeof value === "number" ? value : null;
  }
  return base;
}
